import { useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { useFormContext } from "react-hook-form";

import api from "../../../utils/api";

const PARTS = "/parts";

// /parts/:id is wrapped by PartRoutingMapper which strips the columns we need
// (id, gates, weight, boolean flags). The /summary route returns the raw
// columns specifically for this sync hook.
const usePartById = (id) =>
  useQuery([PARTS, "summary", id], () => api.get(`${PARTS}/${id}/summary`), {
    enabled: !!id,
    staleTime: 60_000,
  });

const pickFromPart = (part) => ({
  partName: part.partName ?? "",
  partNumber: part.partNumber ?? "",
  weight: part.weight ?? 0,
  gates: part.gates ?? 1,
  handBlasting: part.handBlasting ?? 0,
  handDipping: !!part.handDippingRequired,
  flashing: !!part.flashing,
  hardToFixture: !!part.hardToFixture,
  weldRepair: !!part.weldRepair,
  fineDetail: !!part.fineDetail,
  straightening: !!part.straightening,
});

const PartFieldsSync = () => {
  const { watch, setValue, formState } = useFormContext();
  const partId = watch("partId");

  // Track every partId we've observed so far. Anything seen passively (form
  // hydrating from saved data, HubHookFormWrapper resetting after a fetch)
  // is treated as "already in sync" and won't trigger a patch — we only
  // overwrite quote fields when the *user* picks a different part.
  const seenRef = useRef(new Set());
  if (!seenRef.current.has(partId ?? null)) {
    if (!formState.dirtyFields?.partId) {
      seenRef.current.add(partId ?? null);
    }
  }

  const { data: part } = usePartById(partId);

  useEffect(() => {
    if (!partId) return;
    if (seenRef.current.has(partId)) return;
    if (!part || part.id !== partId) return;

    const next = pickFromPart(part);
    Object.entries(next).forEach(([key, val]) => {
      setValue(key, val, { shouldDirty: true, shouldTouch: true });
    });
    seenRef.current.add(partId);
  }, [partId, part, setValue]);

  return null;
};

export default PartFieldsSync;
